/**
 * Environment Configuration
 * 
 * Centralized configuration for all environment-specific settings.
 * Uses Vite's import.meta.env for compile-time environment variables.
 */

export type AppEnvironment = 'dev' | 'stage' | 'prod';

interface EnvironmentConfig {
  // App
  env: AppEnvironment;
  appUrl: string;
  apiUrl: string;
  isDev: boolean;
  isStage: boolean;
  isProd: boolean;
  
  // Supabase
  supabase: {
    url: string;
    anonKey: string;
    projectId: string;
    edgeFunctionsUrl: string;
  };
  
  
  // Sentry
  sentry: {
    dsn: string;
    environment: string;
    enabled: boolean;
    release?: string;
  };
  
  // Amplitude
  amplitude: {
    apiKey: string;
    serverUrl?: string;
    enabled: boolean;
  };
  
  // Feature Flags
  features: {
    debugMode: boolean;
    serviceWorker: boolean;
    consentRequired: boolean;
  };
  
  // File Upload Limits
  uploads: {
    avatar: {
      maxInputSize: number;
      maxDimension: number;
      quality: number;
      maxOutputSize: number;
    };
    adPhoto: {
      maxInputSize: number;
      maxDimension: number;
      quality: number;
      maxOutputSize: number;
    };
    ad: {
      maxPhotos: number;
    };
    chat: {
      maxFileSize: number;
    };
  };
}

function getEnvVar(key: string, defaultValue = ''): string {
  return import.meta.env[key] ?? defaultValue;
}

function getEnvBool(key: string, defaultValue = false): boolean {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
}

function getEnvNumber(key: string, defaultValue: number): number {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

function detectEnvironment(): AppEnvironment {
  const envValue = getEnvVar('VITE_APP_ENV', 'dev');
  if (envValue === 'prod' || envValue === 'production') return 'prod';
  if (envValue === 'stage' || envValue === 'staging') return 'stage';
  return 'dev';
}

// Supabase Cloud fallbacks for Lovable development environment
const SUPABASE_CLOUD_DEFAULTS = {
  VITE_SUPABASE_URL: 'https://elcjqcojzpqxllmcsggv.supabase.co',
  VITE_SUPABASE_PUBLISHABLE_KEY: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVsY2pxY29qenBxeGxsbWNzZ2d2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYzMDQ1MjYsImV4cCI6MjA4MTg4MDUyNn0.6kbIus_tUe56dPCVO9VemVSIddBA0dSl2Emo8PutrTs',
  VITE_SUPABASE_PROJECT_ID: 'elcjqcojzpqxllmcsggv',
} as const;

// Required environment variables - uses cloud fallbacks for dev
function getRequiredEnvVar(key: string, context: string): string {
  const value = import.meta.env[key];
  if (!value) {
    // Use cloud fallbacks for Lovable dev environment
    const fallback = SUPABASE_CLOUD_DEFAULTS[key as keyof typeof SUPABASE_CLOUD_DEFAULTS];
    if (fallback) {
      return fallback;
    }
    console.error(`[Config] Missing required env var: ${key} (${context})`);
    return '';
  }
  return value;
}

function createConfig(): EnvironmentConfig {
  const env = detectEnvironment();
  
  return {
    // App
    env,
    appUrl: getEnvVar('VITE_APP_URL', 'http://localhost:5173'),
    apiUrl: getEnvVar('VITE_API_URL', 'http://localhost:5173/api'),
    isDev: env === 'dev',
    isStage: env === 'stage',
    isProd: env === 'prod',
    
    // Supabase - required for app to function
    supabase: {
      url: getRequiredEnvVar('VITE_SUPABASE_URL', 'Supabase API URL'),
      anonKey: getRequiredEnvVar('VITE_SUPABASE_PUBLISHABLE_KEY', 'Supabase anon key'),
      projectId: getEnvVar('VITE_SUPABASE_PROJECT_ID', 'unknown'),
      // Edge Functions URL - can be different from main URL on self-hosted
      edgeFunctionsUrl: getEnvVar('VITE_SUPABASE_EDGE_FUNCTIONS_URL') 
        || `${getRequiredEnvVar('VITE_SUPABASE_URL', 'Supabase API URL')}/functions/v1`,
    },
    
    
    // Sentry
    sentry: {
      dsn: getEnvVar('VITE_SENTRY_DSN'),
      environment: getEnvVar('VITE_SENTRY_ENVIRONMENT', env),
      enabled: getEnvBool('VITE_SENTRY_ENABLED', true),
      release: getEnvVar('VITE_SENTRY_RELEASE') || undefined,
    },
    
    // Amplitude
    amplitude: {
      apiKey: getEnvVar('VITE_AMPLITUDE_API_KEY'),
      serverUrl: getEnvVar('VITE_AMPLITUDE_SERVER_URL') || undefined,
      enabled: getEnvBool('VITE_AMPLITUDE_ENABLED', true),
    },
    
    // Feature Flags
    features: {
      debugMode: getEnvBool('VITE_DEBUG_MODE', env === 'dev'),
      serviceWorker: getEnvBool('VITE_SW_ENABLED', true),
      consentRequired: getEnvBool('VITE_CONSENT_REQUIRED', true),
    },
    
    // File Upload Limits
    uploads: {
      avatar: {
        maxInputSize: getEnvNumber('VITE_AVATAR_MAX_INPUT_SIZE', 10 * 1024 * 1024),
        maxDimension: getEnvNumber('VITE_AVATAR_MAX_DIMENSION', 512),
        quality: getEnvNumber('VITE_AVATAR_QUALITY', 0.85),
        maxOutputSize: getEnvNumber('VITE_AVATAR_MAX_OUTPUT_SIZE', 200 * 1024),
      },
      adPhoto: {
        maxInputSize: getEnvNumber('VITE_AD_PHOTO_MAX_INPUT_SIZE', 15 * 1024 * 1024),
        maxDimension: getEnvNumber('VITE_AD_PHOTO_MAX_DIMENSION', 1200),
        quality: getEnvNumber('VITE_AD_PHOTO_QUALITY', 0.85),
        maxOutputSize: getEnvNumber('VITE_AD_PHOTO_MAX_OUTPUT_SIZE', 500 * 1024),
      },
      ad: {
        maxPhotos: getEnvNumber('VITE_AD_MAX_PHOTOS', 6),
      },
      chat: {
        maxFileSize: getEnvNumber('VITE_CHAT_MAX_FILE_SIZE', 10 * 1024 * 1024),
      },
    },
  };
}

// Singleton config instance
export const config = createConfig();

// Convenience exports
export const {
  env,
  appUrl,
  apiUrl,
  isDev,
  isStage,
  isProd,
  supabase: supabaseConfig,
  sentry: sentryConfig,
  amplitude: amplitudeConfig,
  features,
  uploads: uploadsConfig,
} = config;

// Helper function to get Edge Function URL
export function getEdgeFunctionUrl(functionName: string): string {
  return `${config.supabase.edgeFunctionsUrl}/${functionName}`;
}

// Helper to get Supabase auth localStorage key
export function getSupabaseAuthKey(): string {
  return `sb-${config.supabase.projectId}-auth-token`;
}

// Configuration validation
if (config.isProd) {
  // Warn if production is using cloud Supabase instead of self-hosted
  if (config.supabase.url.includes('supabase.co')) {
    console.error('[Config ERROR] Production is using cloud Supabase!');
    console.error('[Config] Expected: https://dbase.drugtheapp.ru');
    console.error('[Config] Actual:', config.supabase.url);
    console.error('[Config] This will cause authentication and API failures for moderation.');
  }
  
  // Warn if anon key is missing or placeholder
  if (!config.supabase.anonKey || config.supabase.anonKey.includes('<') || config.supabase.anonKey.length < 50) {
    console.error('[Config ERROR] Invalid or missing Supabase anon key in production!');
  }
  
  // Warn if edge functions URL points to cloud
  if (config.supabase.edgeFunctionsUrl.includes('supabase.co')) {
    console.error('[Config ERROR] Production is using cloud Edge Functions URL!');
    console.error('[Config] Expected: self-hosted URL (e.g., https://dbase.drugtheapp.ru/functions/v1)');
    console.error('[Config] Actual:', config.supabase.edgeFunctionsUrl);
  }
}

// Debug logging in dev
if (config.features.debugMode) {
  console.log('[Config] Environment:', config.env);
  console.log('[Config] App URL:', config.appUrl);
  console.log('[Config] Supabase URL:', config.supabase.url);
  console.log('[Config] Sentry enabled:', config.sentry.enabled);
  console.log('[Config] Amplitude enabled:', config.amplitude.enabled);
}

export default config;
